import React, { useRef, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';

/* ─────────────────────────────────────────────────────────────────────────────
   NoteThread — JIRA-style chronological notes panel
   Oldest note at top, newest at bottom.

   Props:
     assignmentId  Long      — the assignment to show notes for
     fetchNotes    fn(id)    — API call that returns { data: NoteResponse[] }
     postNote      fn(id, { content }) — API call to post a new note
     updateNote    fn(id, noteId, { content }) — API call to edit a note (optional)
     deleteNote    fn(id, noteId)              — API call to delete a note (optional)
     canPost       bool      — whether the current user may add notes
     canModerate   bool      — whether the current user has moderator privileges
                               (OmniDrive Super Admin or Client-Vendor Admin).
                               Combined with currentUserId, this only allows
                               editing/deleting notes the moderator personally
                               authored — not other users' notes.
     currentUserId Long      — id of the logged-in user, used to scope Edit/Delete
                               to notes authored by them
     queryKey      string    — react-query cache key prefix (e.g. 'admin-notes')
─────────────────────────────────────────────────────────────────────────────── */

/* Role-group colour palette */
const GROUP_STYLES = {
  PLATFORM: {
    bubble:  'bg-indigo-50 border-indigo-100',
    badge:   'bg-indigo-100 text-indigo-700',
    avatar:  'bg-indigo-600',
    label:   'OmniDrive',
  },
  VENDOR: {
    bubble:  'bg-amber-50 border-amber-100',
    badge:   'bg-amber-100 text-amber-700',
    avatar:  'bg-amber-500',
    label:   'Vendor',
  },
  CLIENT: {
    bubble:  'bg-emerald-50 border-emerald-100',
    badge:   'bg-emerald-100 text-emerald-700',
    avatar:  'bg-emerald-600',
    label:   'Account',
  },
};

function NoteItem({ note, canModerate, currentUserId, isEditing, onEditStart, onEditCancel, onEditSave, onDelete, isSaving, isDeleting }) {
  // A moderator (super admin) may only edit/delete notes they personally authored.
  const canEditThis = canModerate && currentUserId != null && note.authorId === currentUserId;
  const style = GROUP_STYLES[note.authorGroup] ?? GROUP_STYLES.PLATFORM;
  const initials = note.authorName
    ? note.authorName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const [draft, setDraft] = useState(note.content);

  useEffect(() => {
    if (isEditing) setDraft(note.content);
  }, [isEditing, note.content]);

  const handleSave = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === note.content) return onEditCancel();
    onEditSave(trimmed);
  };

  return (
    <div className="flex gap-3">
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${style.avatar} flex items-center justify-center text-white text-xs font-bold`}>
        {initials}
      </div>

      {/* Bubble */}
      <div className={`flex-1 rounded-xl border px-4 py-3 ${style.bubble}`}>
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="text-sm font-semibold text-gray-900">{note.authorName}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${style.badge}`}>
            {style.label}
          </span>
          <span className="text-xs text-gray-400">{note.authorCompanyName}</span>
          <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
            {note.createdAt
              ? format(new Date(note.createdAt), 'dd MMM yyyy · HH:mm')
              : '—'}
          </span>
          {canEditThis && !isEditing && (
            <span className="flex items-center gap-2 ml-1">
              <button
                type="button"
                onClick={onEditStart}
                className="text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                className="text-xs font-medium text-gray-400 hover:text-red-600 disabled:opacity-40 transition-colors"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </span>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              maxLength={5000}
              autoFocus
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !draft.trim()}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onEditCancel}
                disabled={isSaving}
                className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{note.content}</p>
        )}
      </div>
    </div>
  );
}

export default function NoteThread({
  assignmentId,
  fetchNotes,
  postNote,
  updateNote,
  deleteNote,
  canPost = true,
  canModerate = false,
  currentUserId = null,
  queryKey = 'notes',
}) {
  const qc          = useQueryClient();
  const bottomRef   = useRef(null);
  const [text, setText] = useState('');
  const [editingId, setEditingId] = useState(null);

  const { data, isLoading } = useQuery(
    [queryKey, assignmentId],
    () => fetchNotes(assignmentId),
    { refetchInterval: 30000, enabled: !!assignmentId }
  );

  const notes = data?.data ?? [];

  /* Scroll to bottom whenever notes list grows */
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [notes.length]);

  const mutation = useMutation(
    (content) => postNote(assignmentId, { content }),
    {
      onSuccess: () => {
        toast.success('Note added');
        setText('');
        qc.invalidateQueries([queryKey, assignmentId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to add note'),
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    mutation.mutate(trimmed);
  };

  const handleKeyDown = (e) => {
    /* Ctrl+Enter / Cmd+Enter to submit */
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const editMutation = useMutation(
    ({ noteId, content }) => updateNote(assignmentId, noteId, { content }),
    {
      onSuccess: () => {
        toast.success('Note updated');
        setEditingId(null);
        qc.invalidateQueries([queryKey, assignmentId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update note'),
    }
  );

  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const deleteMutation = useMutation(
    (noteId) => deleteNote(assignmentId, noteId),
    {
      onSuccess: () => {
        toast.success('Note deleted');
        setDeleteTargetId(null);
        qc.invalidateQueries([queryKey, assignmentId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete note'),
    }
  );

  const handleDelete = (noteId) => {
    setDeleteTargetId(noteId);
  };

  return (
    <div className="flex flex-col gap-0">

      {/* ── Thread list ── */}
      <div className="flex flex-col gap-4 max-h-[480px] overflow-y-auto pr-1 pb-2">
        {isLoading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading notes…</p>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center py-10 gap-2">
            <svg className="h-10 w-10 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm text-gray-400">No notes yet — be the first to add one.</p>
          </div>
        ) : (
          notes.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              canModerate={canModerate}
              currentUserId={currentUserId}
              isEditing={editingId === note.id}
              onEditStart={() => setEditingId(note.id)}
              onEditCancel={() => setEditingId(null)}
              onEditSave={(content) => editMutation.mutate({ noteId: note.id, content })}
              onDelete={() => handleDelete(note.id)}
              isSaving={editMutation.isLoading && editingId === note.id}
              isDeleting={deleteMutation.isLoading && deleteMutation.variables === note.id}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Composer ── */}
      {canPost && (
        <form onSubmit={handleSubmit} className="mt-4 border-t border-gray-100 pt-4">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Add a note</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            maxLength={5000}
            placeholder="Type your note… (Ctrl+Enter to submit)"
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-400">{text.length}/5000</span>
            <button
              type="submit"
              disabled={!text.trim() || mutation.isLoading}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
            >
              {mutation.isLoading ? 'Posting…' : 'Add Note'}
            </button>
          </div>
        </form>
      )}

      {/* ── Delete confirmation ── */}
      {deleteTargetId != null && (
        <ConfirmDialog
          title="Delete this note?"
          message="This cannot be undone."
          confirmLabel="Delete"
          confirmClass="bg-red-600 hover:bg-red-700 text-white"
          isLoading={deleteMutation.isLoading}
          onConfirm={() => deleteMutation.mutate(deleteTargetId)}
          onCancel={() => setDeleteTargetId(null)}
        />
      )}
    </div>
  );
}
